import { Typography } from "@mui/material";
import {
  ChangeEvent,
  memo,
  useEffect,
  useMemo,
  useState,
  MouseEvent,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import { EmailRegex } from "../../utils/FormValidationsUtils";
import {
  Box,
  Button,
  ConfirmationDialog,
  FlexColBox,
  Input,
  Table,
  TableColumn,
} from "../../common/components";
import { spacing } from "../../styles/theme";
import DistributorService from "../../service/DistributorService";
import {
  Distributor,
  DistributorRoleAction,
  DistributorStatus,
} from "../../types";
import { SortBy } from "../../common/components/Table/Table";
import { useMoment } from "../../common/hooks/useMoment";
import { toast } from "react-toastify";
import { useThemeBreakpoints } from "../../utils/themeUtils";

const statusColor: Record<DistributorStatus, string> = {
  pending: "#FF9800",
  active: "#4CAF50",
  inactive: "#F44336",
  denied: "#F44336",
};

const DistributorList = () => {
  const { t } = useTranslation();
  const [isRefetching, setIsRefetching] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const { formatDateTime } = useMoment();
  const [email, setEmail] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const { isMobile } = useThemeBreakpoints();
  const [sortBy, setSortBy] = useState<SortBy | undefined>({
    columnId: "createdAt",
    direction: "desc",
  });

  const [confirmDialogType, setConfirmDialogType] = useState<
    { action: DistributorRoleAction; entityId: string } | undefined
  >();

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [distributors, setDistributors] = useState<Distributor[]>([]);

  const isValidEmail = useMemo(() => {
    return EmailRegex.test(email.trim().toLowerCase());
  }, [email]);

  const refetch = useCallback(() => {
    setIsRefetching(true);
    DistributorService.getAllDistributors(page, rowsPerPage, searchTerm, sortBy)
      .then((res) => {
        setDistributors(res.distributors || []);
        setTotalCount(res.count);
      })
      .finally(() => {
        setIsRefetching(false);
        setIsInitialLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm, sortBy]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const onConfirm = async () => {
    if (confirmDialogType) {
      await DistributorService.updateDistributorRole(
        confirmDialogType.entityId,
        confirmDialogType.action
      );
      setIsDialogOpen(false);
      refetch();
    }
  };

  const handleChangePage = (
    _: MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = isNaN(Number(event.target.value))
      ? 50
      : Number(event.target.value);
    setRowsPerPage(newValue);
    setPage(0);
  };

  const onCancelConfirmDialog = () => {
    setIsDialogOpen(false);
  };

  const handleAddNewDistributorEmail = async () => {
    const res = (
      await DistributorService.addNewDistributorEmail(
        email.trim().toLowerCase()
      )
    ).data;
    setEmail("");
    if (res.code === "EMAIL_ALREADY_ADDED") {
      toast.error(t("distributors.add_new_distributor_error"));
    } else if (res.code === "EMAIL_ADDED") {
      toast.success(t("distributors.add_email_to_list_success"));
    } else if (res.code === "EMAIL_ALREADY_A_DISTRIBUTOR") {
      toast.error(t("distributors.user_already_a_distributor"));
    } else if (res.code === "EMAIL_GRANTED") {
      toast.success(t("distributors.grant_email_success"));
      refetch();
    }
  };

  const columns: TableColumn<Distributor>[] = [
    {
      id: "firstName",
      labelKey: "distributors.table.name",
      sortable: true,
      customSortKey: "name",
      width: "20%",
      render: (row) => (
        <Typography variant="textRegular">{`${row.firstName} ${row.lastName}`}</Typography>
      ),
    },
    {
      id: "email",
      labelKey: "distributors.table.email",
      sortable: true,
      width: "25%",
    },
    {
      id: "createdAt",
      labelKey: "distributors.table.createdAt",
      sortable: true,
      format: (value) => formatDateTime(value),
    },
    {
      id: "status",
      sortable: true,
      labelKey: "distributors.table.status",
      render: (row) => (
        <Typography variant="textRegular" color={statusColor[row.status]}>
          {t(`distributors.status.${row.status}`)}
        </Typography>
      ),
    },
    {
      id: "action",
      labelKey: "distributors.table.action",
      width: "50px",
      align: "right",
      render: (row) => (
        <Box display="flex" gap={"8px"} maxWidth={"fit-content"}>
          {row.status === "pending" && (
            <>
              <Button
                size="small"
                onClick={() => {
                  setConfirmDialogType({
                    action: "promote",
                    entityId: row.id,
                  });
                  setIsDialogOpen(true);
                }}
              >
                {t("distributors.actions.promote")}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setConfirmDialogType({
                    action: "deny",
                    entityId: row.id,
                  });
                  setIsDialogOpen(true);
                }}
              >
                {t("distributors.actions.deny")}
              </Button>
            </>
          )}
          {row.status === "active" && (
            <Button
              size="small"
              onClick={() => {
                setConfirmDialogType({ action: "revoke", entityId: row.id });
                setIsDialogOpen(true);
              }}
            >
              {t("distributors.actions.revoke")}
            </Button>
          )}
          {(row.status === "inactive" || row.status === "denied") && (
            <Button
              size="small"
              onClick={() => {
                setConfirmDialogType({ action: "promote", entityId: row.id });
                setIsDialogOpen(true);
              }}
            >
              {t("distributors.actions.promote")}
            </Button>
          )}
        </Box>
      ),
    },
  ];

  return (
    <FlexColBox
      p={{
        xs: `${spacing.regularMobile} ${spacing.largeMobile} ${spacing.xlMobile}`,
        lg: `${spacing.regularDesktop} ${spacing.largeDesktop} ${spacing.xlDesktop}`,
      }}
      gap={{ xs: spacing.groupingMobile, lg: spacing.groupingDesktop }}
      fullWidth
    >
      <Box
        display="flex"
        flexDirection={{ xs: "column", lg: "row" }}
        alignItems="center"
        justifyContent="space-between"
        gap={"16px"}
      >
        <Typography
          variant="title"
          mb={{ xs: spacing.subtitleMobile, lg: spacing.subtitleDesktop }}
          fontWeight="medium"
        >
          {t("distributors.title")}
        </Typography>
        <Box
          display="flex"
          alignItems="center"
          gap={"8px"}
          fullWidth={isMobile}
        >
          <Input
            placeholder={t("distributors.add_new_distributor")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ width: { xs: "100%", lg: "375px" } }}
          />
          <Button
            onClick={handleAddNewDistributorEmail}
            size="small"
            sx={{ height: "40px" }}
            disabled={!isValidEmail}
          >
            {t("general.add")}
          </Button>
        </Box>
      </Box>
      <Table
        isInitialLoading={isInitialLoading}
        isRefetching={isRefetching}
        columns={columns}
        data={distributors}
        rowsPerPage={rowsPerPage}
        page={page}
        sortBy={sortBy}
        totalCount={totalCount}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        handleSearch={setSearchTerm}
        handleSort={setSortBy}
        containerSx={{
          minHeight: "0",
        }}
      />
      <ConfirmationDialog
        open={isDialogOpen}
        onClose={onCancelConfirmDialog}
        onConfirm={onConfirm}
        title={
          confirmDialogType?.action
            ? t(`distributors.confirmation.${confirmDialogType?.action}.title`)
            : ""
        }
        description={
          confirmDialogType?.action
            ? t(
                `distributors.confirmation.${confirmDialogType?.action}.description`
              )
            : ""
        }
      />
    </FlexColBox>
  );
};

export default memo(DistributorList);
